import { RichTextAdvancedInput } from '@framework/engine/inputs/rich-text-advanced/RichTextAdvancedInput.tsx';
import { Datatype } from '@framework/engine/schema/expressions/datatype/Datatype.ts';
import { AssignableExpressionInput } from '@framework/engine/schema/expressions/repository/assignable/input/AssignableExpressionInput.tsx';
import { type IWidgetComponentProps } from '@framework/engine/schema/widgets/IWidgetType.ts';
import { type IWidgetConfigurationSlot } from '@framework/engine/schema/widgets/repository/common/config-slots/IWidgetConfigurationSlot.ts';
import { WidgetConfigurationSlotRender } from '@framework/engine/schema/widgets/repository/common/config-slots/WidgetConfigurationSlotRender.tsx';
import { CommonConfigComponent } from '@framework/engine/schema/widgets/repository/inputs/common/CommonInputConfigComponent.tsx';
import { useTreeReferencesContext } from '@framework/engine/tree/references/TreeReferencesContext.tsx';
import { MenuPresets } from '@framework/form/inputs/rich-text/menu/MenuPresets.tsx';
import { TextInput } from '@framework/form/inputs/TextInput.tsx';
import { FormItem } from '@framework/form/item/FormItem.tsx';
import { TbIcon } from '@framework/ui/components/icon/TbIcon.tsx';
import { IconBuildingCommunity, IconMapPin } from '@tabler/icons-react';
import { type IProvinceWidget } from './IProvinceWidget.ts';
import { MunicipalityGroupListSelectInput } from './MunicipalityGroupListSelectInput.tsx';

/** Card "Opzioni Provincia": testi del campo provincia. */
const ProvinceOptionsSlot: IWidgetConfigurationSlot = {
  key: 'province-options',
  title: 'Opzioni Provincia',
  icon: <TbIcon icon={IconMapPin} />,
  description: 'Etichetta, testo di aiuto e placeholder del campo provincia.',
  order: 15,
};

/** Card "Opzioni Comune": testi e filtri del campo comune. */
const TownOptionsSlot: IWidgetConfigurationSlot = {
  key: 'town-options',
  title: 'Opzioni Comune',
  icon: <TbIcon icon={IconBuildingCommunity} />,
  description:
    'Etichetta, testo di aiuto, placeholder e gruppo comuni del campo comune.',
  order: 16,
};

export interface IProvinceWidgetConfigComponentProps
  extends IWidgetComponentProps<IProvinceWidget> {}

export function ProvinceWidgetConfigComponent(
  props: IProvinceWidgetConfigComponentProps,
) {
  const { references } = useTreeReferencesContext();

  return (
    <>
      <CommonConfigComponent
        {...props}
        hideConfigPlaceholder
        hideConfigHelpText
        hideConfigDefaultValue
        hideConfigLabel
        noConfirmation
      />
      <FormItem name="defaultProvince" label="Codice provincia di default">
        <AssignableExpressionInput
          field={{ type: Datatype.String }}
          references={references}
          name="defaultProvince"
          block
          size="large"
        />
      </FormItem>
      <FormItem name="defaultTown" label="Codice comune di default">
        <AssignableExpressionInput
          field={{ type: Datatype.String }}
          references={references}
          name="defaultTown"
          block
          size="large"
        />
      </FormItem>

      <WidgetConfigurationSlotRender slot={ProvinceOptionsSlot}>
        <FormItem name="provinceLabel" label="Etichetta provincia">
          <RichTextAdvancedInput
            name="provinceLabel"
            rows={1}
            menus={MenuPresets.Minimal}
          />
        </FormItem>

        <FormItem name="provinceHelpText" label="Testo aiuto provincia">
          <RichTextAdvancedInput
            name="provinceHelpText"
            rows={1}
            menus={MenuPresets.Minimal}
          />
        </FormItem>

        <FormItem name="provincePlaceholder" label="Placeholder provincia">
          <TextInput name="provincePlaceholder" />
        </FormItem>
      </WidgetConfigurationSlotRender>

      <WidgetConfigurationSlotRender slot={TownOptionsSlot}>
        <FormItem name="townLabel" label="Etichetta comune">
          <RichTextAdvancedInput
            name="townLabel"
            rows={1}
            menus={MenuPresets.Minimal}
          />
        </FormItem>

        <FormItem name="townHelpText" label="Testo aiuto comune">
          <RichTextAdvancedInput
            name="townHelpText"
            rows={1}
            menus={MenuPresets.Minimal}
          />
        </FormItem>

        <FormItem name="townPlaceholder" label="Placeholder Comune">
          <TextInput name="townPlaceholder" />
        </FormItem>

        <FormItem
          name="municipalityGroupList"
          label="Gruppo Comuni"
          help="Selezionando un gruppo, verranno filtrate le province e i comuni disponibili nei menù a tendina."
        >
          <MunicipalityGroupListSelectInput name="municipalityGroupList" />
        </FormItem>
      </WidgetConfigurationSlotRender>
    </>
  );
}
